import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  alpha,
  useTheme,
  Grid,
} from "@mui/material";
import { Edit, CheckCircle } from "@mui/icons-material";
import CustomerInfoForm from "./CustomerInfoForm";
import PaymentMethodSelector from "./PaymentMethodSelector";
import PaymentSplitDialog from "./PaymentSplitDialog";
import DiscountSelector from "./DiscountSelector";
import BillReceipt from "./BillReceipt";
import OrderSuccessDialog from "./OrderSuccessDialog";
import CashPaymentDialog from "./CashPaymentDialog";
import OrderService from "../../../../services/OrderService";
import PaymentService from "../../../../services/PaymentService";
import CompanyServices from "../../../../services/CompanyService";
import CompanyConfigurationServices from "../../../../services/CompanyConfigurationService";
import { toast } from "react-toastify";
import { useUserWithCompany } from "hooks/useCompany";
import { calculateOrderTotals } from "../../utils/cartUtils";
import { useDispatch } from "react-redux";

/**
 * OrderCheckoutDialog Component
 * Fullscreen dialog for order checkout with customer info, payment, and receipt
 */
const OrderCheckoutDialog = ({
  open,
  onClose,
  table,
  cart,
  total,
  onConfirmOrder,
  onEditOrder,
  existingOrderId,
  existingOrderNumber,
  existingTableName,
}) => {
  const {
    companyId,
    vatRate,
    serviceChargeRateDining,
    serviceChargeRateTakeaway,
    currency,
    taxId,
    company,
    user,
    userType,
  } = useUserWithCompany();
  const theme = useTheme();
  const dispatch = useDispatch();

  // Check if user is a waiter
  const isWaiter = userType === "waiter" || user?.type === "waiter";

  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  // For waiters, always set payment method to "none"
  const [paymentMethod, setPaymentMethod] = useState(
    isWaiter ? "none" : "none",
  );
  const [payments, setPayments] = useState([]);
  const [showPaymentSplit, setShowPaymentSplit] = useState(false);
  // For waiters, always set order type to "dine-in"
  const [orderType, setOrderType] = useState(
    isWaiter ? "dine-in" : table ? "dine-in" : "takeaway",
  );
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [showCashPayment, setShowCashPayment] = useState(false);
  const [cashPaymentInfo, setCashPaymentInfo] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [orderTypeConfig, setOrderTypeConfig] = useState({
    Dinein: true,
    Takeaway: true,
    Delivery: true,
  });

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setCustomerName("");
      setCustomerMobile("");
      setSpecialInstructions("");
      // For waiters, always reset to "none" payment method; for existing orders default to "cash"
      setPaymentMethod(existingOrderId ? "cash" : isWaiter ? "none" : "none");
      setPayments([]);
      setShowPaymentSplit(false);
      // For waiters, always reset to "dine-in" order type
      setOrderType(isWaiter ? "dine-in" : table ? "dine-in" : "takeaway");
      setDiscountType("none");
      setDiscountValue("");
      setDiscountCode("");
      setSubmitting(false);
      setOrderResult(null);
      setShowCashPayment(false);
      setCashPaymentInfo(null);
    }
  }, [open, table, isWaiter, existingOrderId]);

  React.useEffect(() => {
    if (!open || !companyId) return;

    CompanyConfigurationServices.getConfigurations(companyId, "order_settings")
      .then((response) => {
        if (!response?.success) return;

        const items =
          response?.data?.view?.data ??
          response?.data?.view?.items ??
          response?.data?.items ??
          [];

        if (items.length > 0) {
          const value = items[0]?.value || {};
          setOrderTypeConfig({
            Dinein: Boolean(value.Dinein),
            Takeaway: Boolean(value.Takeaway),
            Delivery: Boolean(value.Delivery),
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching POS checkout order type settings:", error);
      });
  }, [open, companyId]);

  // Fetch company data if not available in Redux or if rates are 0
  React.useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId) return;

      // Check if company data is missing or rates are all 0
      const needsFetch =
        !company ||
        (vatRate === 0 &&
          serviceChargeRateDining === 0 &&
          serviceChargeRateTakeaway === 0);

      if (needsFetch && !loadingCompany) {
        console.log("Fetching company data from API...");
        setLoadingCompany(true);
        try {
          const response = await CompanyServices.getById(companyId);
          console.log("Company API Response:", response);

          if (response?.data?.view) {
            setCompanyData(response.data.view);
            // Update Redux store with company data
            dispatch({
              type: "UPDATE_COMPANY",
              payload: response.data.view,
            });
          }
        } catch (error) {
          console.error("Error fetching company data:", error);
          toast.error("Failed to load company settings");
        } finally {
          setLoadingCompany(false);
        }
      }
    };

    if (open) {
      fetchCompanyData();
    }
  }, [
    open,
    companyId,
    company,
    vatRate,
    serviceChargeRateDining,
    serviceChargeRateTakeaway,
    loadingCompany,
    dispatch,
  ]);

  // Use fetched company data or fallback to Redux data
  const activeVatRate = companyData?.vatRate
    ? parseFloat(companyData.vatRate)
    : vatRate;
  const activeServiceChargeRateDining = companyData?.serviceChargeRateDining
    ? parseFloat(companyData.serviceChargeRateDining)
    : serviceChargeRateDining;
  const activeServiceChargeRateTakeaway = companyData?.serviceChargeRateTakeaway
    ? parseFloat(companyData.serviceChargeRateTakeaway)
    : serviceChargeRateTakeaway;
  const activeCurrency = companyData?.currency || currency;
  const activeTaxId = companyData?.taxId || taxId;

  // Calculate cart subtotal first
  const cartSubtotal = calculateOrderTotals(cart, orderType, {
    serviceChargeRateDining: activeServiceChargeRateDining,
    serviceChargeRateTakeaway: activeServiceChargeRateTakeaway,
    vatRate: activeVatRate,
  }).subtotal;

  // Calculate discount amount on subtotal
  const calculateDiscountAmount = () => {
    if (discountType === "none" || !discountValue) return 0;

    if (discountType === "percentage") {
      return (cartSubtotal * Number(discountValue)) / 100;
    }
    if (discountType === "amount" || discountType === "fixed") {
      return Math.min(Number(discountValue), cartSubtotal); // Can't discount more than subtotal
    }

    return 0;
  };

  const discountAmount = calculateDiscountAmount();
  const discountedSubtotal = cartSubtotal - discountAmount;

  // Calculate service charge and VAT separately on the discounted subtotal
  const serviceChargeRate =
    orderType === "dine-in"
      ? activeServiceChargeRateDining
      : activeServiceChargeRateTakeaway;

  // Service Charge calculated on discounted subtotal
  const serviceCharge = (discountedSubtotal * serviceChargeRate) / 100;

  // VAT calculated on discounted subtotal ONLY (not including service charge)
  const vatAmount = (discountedSubtotal * activeVatRate) / 100;

  // Final total = discounted subtotal + service charge + VAT
  const finalTotal = discountedSubtotal + serviceCharge + vatAmount;

  // Create orderTotals object for compatibility with existing code
  const orderTotals = {
    subtotal: cartSubtotal,
    discountAmount: discountAmount,
    discountedSubtotal: discountedSubtotal,
    serviceChargeRate: serviceChargeRate,
    serviceCharge: serviceCharge,
    vatRate: activeVatRate,
    vatAmount: vatAmount,
    grandTotal: finalTotal,
    orderType: orderType,
  };

  console.log("Active vatRate:", activeVatRate);
  console.log("Active serviceChargeRateDining:", activeServiceChargeRateDining);
  console.log(
    "Active serviceChargeRateTakeaway:",
    activeServiceChargeRateTakeaway,
  );
  console.log("Calculated Order Totals (VAT on Subtotal only):", orderTotals);

  // Auto-set payment amount when single method selected
  React.useEffect(() => {
    if (paymentMethod !== "none" && paymentMethod !== "split") {
      setPayments([{ method: paymentMethod, amount: finalTotal }]);
    } else if (paymentMethod === "none") {
      setPayments([]);
    }
  }, [paymentMethod, finalTotal]);

  // Calculate total allocated payment amount
  const totalPaymentAllocated = payments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0,
  );

  // Check if payment is valid
  const isPaymentValid =
    paymentMethod === "none" ||
    (paymentMethod === "split" &&
      payments.length > 0 &&
      Math.abs(totalPaymentAllocated - finalTotal) < 0.01) ||
    (paymentMethod !== "split" && payments.length > 0);

  const handleConfirmOrder = async () => {
    try {
      setSubmitting(true);

      // ── Existing order: only add payment, skip order creation ──
      if (existingOrderId) {
        if (payments && payments.length > 0) {
          const paymentData = {
            companyId: companyId,
            payments: payments.map((payment) => ({
              orderId: existingOrderId,
              paymentMethod: payment.method,
              amount: Number(Number(payment.amount).toFixed(2)),
              status: "completed",
              paymentDate: new Date().toISOString(),
              notes: payment.notes || `Payment via ${payment.method}`,
              ...(payment.transactionId && {
                transactionId: payment.transactionId,
              }),
            })),
          };
          await PaymentService.create(paymentData);
        }
        // Skip success/print dialog — call parent directly so it can show its own completion UI
        onConfirmOrder({ id: existingOrderId });
        onClose();
        return;
      }
      const items = cart.map((cartItem) => {
        // Check if it's an offer
        if (cartItem.isOffer) {
          return {
            offerId: cartItem.offerId,
            quantity: cartItem.quantity,
          };
        }

        // Regular menu item
        const item = {
          menuItemId: cartItem.id,
          quantity: cartItem.quantity,
        };

        // Add variation if selected
        if (cartItem.selectedVariation) {
          item.variationId = cartItem.selectedVariation.id;
        }

        // Add modifiers if selected
        if (
          cartItem.selectedModifiers &&
          cartItem.selectedModifiers.length > 0
        ) {
          item.modifiers = cartItem.selectedModifiers.map((m) => m.id);
        }

        // Add special instructions if any
        if (cartItem.specialInstructions) {
          item.specialInstructions = cartItem.specialInstructions;
        }

        return item;
      });

      // Build order data
      const orderData = {
        companyId: companyId,
        customerName,
        customerMobile,
        orderType,
        addType: "pos",
        items,
        checkStock: true,
      };

      // Add special instructions if provided
      if (specialInstructions && specialInstructions.trim()) {
        orderData.specialInstructions = specialInstructions.trim();
      }

      // Add tableId only for dine-in orders
      if (table?.id) {
        orderData.tableId = table.id;
      }

      // Add discount code if selected
      if (discountCode) {
        orderData.discountCode = discountCode;
      }

      // Add payment status based on payment method
      orderData.paymentStatus =
        paymentMethod === "none" ? "pending" : "completed";

      // Add order totals breakdown (with discount applied first, then service charge and VAT)
      orderData.subtotal = Number(orderTotals.subtotal.toFixed(2));

      // Add discount amount if applicable
      if (orderTotals.discountAmount > 0) {
        orderData.discountAmount = Number(
          orderTotals.discountAmount.toFixed(2),
        );
      }

      orderData.serviceCharge = Number(orderTotals.serviceCharge.toFixed(2));
      orderData.serviceChargeRate = orderTotals.serviceChargeRate;
      orderData.vatAmount = Number(orderTotals.vatAmount.toFixed(2));
      orderData.vatRate = orderTotals.vatRate;

      // Add total amount (final total after discounts, includes VAT and service charge)
      orderData.totalAmount = Number(finalTotal.toFixed(2));

      // Add payments array with proper format
      if (payments && payments.length > 0) {
        orderData.payments = payments.map((payment) => ({
          paymentMethod: payment.method,
          amount: Number(Number(payment.amount).toFixed(2)),
          status: "completed",
          paymentDate: new Date().toISOString(),
          notes: payment.notes || `Payment via ${payment.method}`,
        }));
      }

      // Log order data for debugging
      console.log("Order Data being sent:", {
        ...orderData,
        calculationBreakdown: {
          subtotal: orderData.subtotal,
          serviceCharge: orderData.serviceCharge,
          serviceChargeRate: orderData.serviceChargeRate,
          vatAmount: orderData.vatAmount,
          vatRate: orderData.vatRate,
          discountAmount: orderData.discountAmount || 0,
          totalAmount: orderData.totalAmount,
        },
      });

      // Create order via API
      const response = await OrderService.createOrder(orderData);

      console.log("Order API Response:", response);

      if (!response?.success) {
        const apiMessage =
          response?.message ||
          response?.error?.error ||
          response?.error?.message ||
          (typeof response?.error === "string" ? response.error : null);
        throw new Error(apiMessage || "Failed to place order");
      }

      if (response?.data?.inserted) {
        let orderForResult = response.data.inserted;
        console.log("Setting order result:", orderForResult);

        // Call payment API if payments exist
        if (payments && payments.length > 0) {
          try {
            const paymentData = {
              companyId: companyId,
              payments: payments.map((payment) => ({
                orderId: orderForResult.id,
                paymentMethod: payment.method,
                amount: Number(Number(payment.amount).toFixed(2)),
                status: "completed",
                paymentDate: new Date().toISOString(),
                notes: payment.notes || `Payment via ${payment.method}`,
                ...(payment.transactionId && {
                  transactionId: payment.transactionId,
                }),
              })),
            };

            console.log("Payment API Request:", paymentData);
            const paymentResponse = await PaymentService.create(paymentData);
            console.log("Payment API Response:", paymentResponse);

            const insertedPayments = paymentResponse?.data?.inserted;
            if (insertedPayments) {
              const paymentRecords = Array.isArray(insertedPayments)
                ? insertedPayments
                : [insertedPayments];
              orderForResult = {
                ...orderForResult,
                Payments: paymentRecords,
              };
            } else {
              orderForResult = {
                ...orderForResult,
                Payments: paymentData.payments,
              };
            }

            // Show success message only after both APIs succeed
            toast.success("Order and payment recorded successfully!");
          } catch (paymentError) {
            console.error("Error recording payment:", paymentError);
            toast.error(
              paymentError.response?.data?.message ||
              "Order placed but payment recording failed. Please contact support.",
            );
            // Still show order result even if payment API fails
            orderForResult = {
              ...orderForResult,
              Payments: payments.map((payment) => ({
                paymentMethod: payment.method,
                amount: payment.amount,
              })),
            };
          }
        } else {
          // No payment to record
          toast.success("Order placed successfully!");
        }

        // Show cash payment dialog only for single cash payment (not for split)
        if (paymentMethod === "cash") {
          // Show cash payment dialog first
          setOrderResult(orderForResult);
          setShowCashPayment(true);
        } else {
          // Show order success dialog directly for all other cases
          setOrderResult(orderForResult);
        }
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to place order. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isSelectedOrderTypeAllowed =
    existingOrderId ||
    (orderType === "dine-in" && orderTypeConfig.Dinein) ||
    (orderType === "takeaway" && orderTypeConfig.Takeaway) ||
    (orderType === "delivery" && orderTypeConfig.Delivery);

  // Validation - customer info is optional
  const isValid = isPaymentValid && isSelectedOrderTypeAllowed;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex" }}>
        {/* Left Panel - Forms */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                width: "300px",
                height: "300px",
                borderRadius: "50%",
                background: alpha("#fff", 0.05),
                transform: "translate(30%, -30%)",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                background: alpha("#fff", 0.03),
                transform: "translate(-30%, 30%)",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              {/* Left: icon + title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(10px)",
                    border: `2px solid ${alpha("#fff", 0.2)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 4px 16px ${alpha("#000", 0.1)}`,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 28, color: "white" }} />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1,
                      mb: 0.5,
                      textShadow: `0 2px 12px ${alpha("#000", 0.2)}`,
                    }}
                  >
                    {existingOrderId ? "Add Payment" : "Confirm Order"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, opacity: 0.85 }}
                  >
                    {existingOrderId
                      ? "Record payment for this order"
                      : "Complete your order details"}
                  </Typography>
                </Box>
              </Box>

              {/* Right: order detail cards — only shown for existing orders */}
              {existingOrderId && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    ml: "auto",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  {existingOrderNumber && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: "12px",
                        bgcolor: alpha("#fff", 0.12),
                        border: `1.5px solid ${alpha("#fff", 0.25)}`,
                        backdropFilter: "blur(8px)",
                        minWidth: 110,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: alpha("#fff", 0.7),
                          letterSpacing: "0.8px",
                          textTransform: "uppercase",
                          mb: 0.2,
                        }}
                      >
                        Order No.
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 800,
                          color: "white",
                          lineHeight: 1,
                        }}
                      >
                        #{existingOrderNumber}
                      </Typography>
                    </Box>
                  )}
                  {existingTableName && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: "12px",
                        bgcolor: alpha("#fff", 0.12),
                        border: `1.5px solid ${alpha("#fff", 0.25)}`,
                        backdropFilter: "blur(8px)",
                        minWidth: 110,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: alpha("#fff", 0.7),
                          letterSpacing: "0.8px",
                          textTransform: "uppercase",
                          mb: 0.2,
                        }}
                      >
                        Table
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 800,
                          color: "white",
                          lineHeight: 1,
                        }}
                      >
                        {existingTableName}
                      </Typography>
                    </Box>
                  )}
                  {total > 0 && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: "12px",
                        bgcolor: alpha("#fff", 0.18),
                        border: `1.5px solid ${alpha("#fff", 0.3)}`,
                        backdropFilter: "blur(8px)",
                        minWidth: 110,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: alpha("#fff", 0.7),
                          letterSpacing: "0.8px",
                          textTransform: "uppercase",
                          mb: 0.2,
                        }}
                      >
                        Amount Due
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 800,
                          color: "white",
                          lineHeight: 1,
                        }}
                      >
                        {currency} {parseFloat(total).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Forms Content - Scrollable Area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              background: `linear-gradient(180deg, ${alpha(theme.palette.grey[50], 0.4)} 0%, ${alpha(theme.palette.grey[100], 0.2)} 100%)`,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha(theme.palette.primary.main, 0.4),
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.6),
                },
              },
            }}
          >
            <Box sx={{ height: "100%" }}>
              <Grid container spacing={2.5} sx={{ height: "100%" }}>
                {/* Column 1: Customer Info */}
                <Grid size={{ xs: 12, lg: 4 }}>
                  <CustomerInfoForm
                    customerName={customerName}
                    setCustomerName={setCustomerName}
                    customerMobile={customerMobile}
                    setCustomerMobile={setCustomerMobile}
                    specialInstructions={specialInstructions}
                    setSpecialInstructions={setSpecialInstructions}
                  />
                </Grid>

                {/* Column 2: Discount */}
                <Grid size={{ xs: 12, lg: 8 }}>
                  <DiscountSelector
                    discountType={discountType}
                    setDiscountType={setDiscountType}
                    discountValue={discountValue}
                    setDiscountValue={setDiscountValue}
                    discountCode={discountCode}
                    setDiscountCode={setDiscountCode}
                    orderTotal={orderTotals.subtotal}
                    hasCustomerMobile={customerMobile.length === 10}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Footer - Payment Methods and Action Buttons */}
          <Box
            sx={{
              p: 2.5,
              background: `linear-gradient(to top, ${alpha(theme.palette.grey[900], 0.03)} 0%, ${alpha(theme.palette.grey[50], 0.5)} 50%, transparent 100%)`,
              borderTop: `2px solid ${alpha(theme.palette.divider, 0.15)}`,
              boxShadow: `0 -6px 30px ${alpha(theme.palette.grey[900], 0.1)}`,
            }}
          >
            <Grid container spacing={2.5}>
              {/* Left Section: Payment Methods */}
              <Grid size={{ xs: 12, md: 7 }}>
                <PaymentMethodSelector
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  onSplitPaymentClick={() => {
                    setPaymentMethod("split");
                    setShowPaymentSplit(true);
                  }}
                  isWaiter={existingOrderId ? false : isWaiter}
                  hideNone={Boolean(existingOrderId)}
                />
              </Grid>

              {/* Right Section: Action Buttons */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    height: "100%",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      flex: 1,
                      height: "100%",
                      minHeight: 80,
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: "none",
                      borderWidth: 2,
                      borderColor: alpha(theme.palette.grey[400], 0.5),
                      color: theme.palette.text.primary,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.15)}`,
                      "&:hover": {
                        borderWidth: 2,
                        borderColor: theme.palette.grey[600],
                        bgcolor: alpha(theme.palette.grey[100], 0.8),
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 16px ${alpha(theme.palette.grey[500], 0.2)}`,
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleConfirmOrder}
                    disabled={!isValid || submitting}
                    sx={{
                      flex: 2,
                      height: "100%",
                      minHeight: 80,
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: "none",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      background: isValid
                        ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                        : theme.palette.action.disabledBackground,
                      boxShadow: isValid
                        ? `0 6px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                        : "none",
                      "&:hover": {
                        boxShadow: isValid
                          ? `0 8px 32px ${alpha(theme.palette.primary.main, 0.5)}`
                          : "none",
                        transform: isValid ? "translateY(-2px)" : "none",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {submitting ? (
                      existingOrderId ? (
                        "Completing Order..."
                      ) : (
                        "Placing Order..."
                      )
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 32 }} />
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {existingOrderId ? "Complete Order" : "Confirm Order"}
                        </Typography>
                      </>
                    )}
                  </Button>
                </Box>
                {!isSelectedOrderTypeAllowed && (
                  <Typography
                    sx={{
                      mt: 1.25,
                      fontSize: "0.82rem",
                      color: theme.palette.error.main,
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  >
                    Selected order type is disabled in company settings.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Right Panel - Receipt */}
        <Box
          sx={{
            width: 480,
            bgcolor: "#fafafa",
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            flexDirection: "column",
            boxShadow: `-8px 0 32px ${alpha(theme.palette.grey[900], 0.08)}`,
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              position: "relative",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Order Summary
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600, mt: 0.5 }}
            >
              Review your order details
            </Typography>

            {/* Edit Button */}
            <IconButton
              onClick={() => {
                if (onEditOrder) {
                  onEditOrder(cart, table);
                } else {
                  onClose();
                }
              }}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                color: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  transform: "scale(1.1)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <Edit sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            <BillReceipt
              table={table}
              cart={cart}
              customerName={customerName}
              customerMobile={customerMobile}
              payments={payments}
              orderType={orderType}
              subtotal={orderTotals.subtotal}
              serviceCharge={orderTotals.serviceCharge}
              serviceChargeRate={orderTotals.serviceChargeRate}
              vatAmount={orderTotals.vatAmount}
              vatRate={orderTotals.vatRate}
              taxId={activeTaxId}
              discountType={discountType}
              discountValue={discountValue}
              discountAmount={orderTotals.discountAmount}
              total={finalTotal}
              currency={activeCurrency}
            />
          </Box>
        </Box>
      </Box>

      {/* Payment Split Dialog */}
      <PaymentSplitDialog
        open={showPaymentSplit}
        onClose={() => setShowPaymentSplit(false)}
        onConfirm={() => {
          setShowPaymentSplit(false);
          setPaymentMethod("split");
          // Automatically trigger order placement after split dialog confirms
          setTimeout(() => handleConfirmOrder(), 100);
        }}
        payments={payments}
        setPayments={setPayments}
        orderTotal={finalTotal}
      />

      {/* Cash Payment Dialog */}
      <CashPaymentDialog
        open={showCashPayment}
        onClose={() => {
          setShowCashPayment(false);
          setCashPaymentInfo(null);
        }}
        onConfirm={(paymentInfo) => {
          setCashPaymentInfo(paymentInfo);
          setShowCashPayment(false);
        }}
        totalAmount={finalTotal}
      />

      {/* Success Dialog */}
      <OrderSuccessDialog
        open={Boolean(orderResult) && !showCashPayment}
        onClose={() => {
          // Call parent callback when success dialog closes
          if (orderResult) {
            onConfirmOrder(orderResult);
          }
          setOrderResult(null);
          setCashPaymentInfo(null);
          onClose();
        }}
        orderResult={orderResult}
        cashPaymentInfo={cashPaymentInfo}
      />
    </Dialog>
  );
};

export default OrderCheckoutDialog;
